import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  List,
  Typography,
  ListItemButton,
  IconButton,
  CircularProgress,
  ListItemText,
  Divider,
} from "@mui/material";
import { ArrowLeft, BookOpen, Hash } from "lucide-react";

const StatuteDetailSidebar = ({ onSectionClick, selectedSection, onClose }) => {
  const statuteSections = useSelector((state) => state.statuteSections);
  const { loading, sections } = statuteSections;

  const formatStatuteSection = (title) => {
    if (!title) return;
    const titleCaseTitle = title
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  return (
    <Box
      sx={{
        display: { xs: "none", lg: "block" },
        width: "300px",
        height: "100%",
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #F1F5F9",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        marginLeft: { xs: 0, lg: 2 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#F0F9FF",
              color: "#00509e",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                color: "#64748B",
                backgroundColor: "#F8FAFC",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  color: "#1E293B",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <ArrowLeft size={18} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#1E293B",
              fontFamily:
                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            Table of Contents
          </Typography>

          <BookOpen size={18} />
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          height: "calc(100% - 73px)",
          overflowY: "auto",
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "#F8FAFC",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "8px",
            "&:hover": {
              background: "#94A3B8",
            },
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 20px",
            }}
          >
            <CircularProgress size={24} sx={{ color: "#00509e" }} />
          </Box>
        ) : sections?.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 20px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#FEF3C7",
                color: "#D97706",
                marginBottom: "16px",
              }}
            >
              <Hash size={24} />
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#64748B",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                marginBottom: "4px",
              }}
            >
              No Sections Available
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#94A3B8",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              This statute doesn't have any sections to display.
            </Typography>
          </Box>
        ) : (
          <List sx={{ padding: "8px 0" }}>
            {sections?.[0]?.related_data
              ?.sort((a, b) => {
                if (a.Section === "PREAMBLE") return -1;
                if (b.Section === "PREAMBLE") return 1;
                const sectionA = parseFloat(a.Section);
                const sectionB = parseFloat(b.Section);
                return sectionA - sectionB;
              })
              .map((result, index) => (
                <React.Fragment key={result.id}>
                  <ListItemButton
                    sx={{
                      margin: "4px 16px",
                      padding: "10px 14px",
                      borderRadius: "12px",
                      backgroundColor:
                        selectedSection === index ? "#F0F9FF" : "transparent",
                      border:
                        selectedSection === index
                          ? "1px solid #BFDBFE"
                          : "1px solid transparent",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          selectedSection === index ? "#E0F2FE" : "#F8FAFC",
                        borderColor:
                          selectedSection === index ? "#93C5FD" : "#E2E8F0",
                      },
                      "&:active": {
                        backgroundColor: "#DBEAFE",
                        transform: "scale(0.98)",
                      },
                    }}
                    onClick={() => onSectionClick(index)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          backgroundColor:
                            selectedSection === index ? "#00509e" : "#F1F5F9",
                          color:
                            selectedSection === index ? "#FFFFFF" : "#64748B",
                          fontSize: "12px",
                          fontWeight: "600",
                          fontFamily:
                            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                          flexShrink: 0,
                        }}
                      >
                        {result.Section === "PREAMBLE" ? "0" : result.Section}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                selectedSection === index ? "600" : "500",
                              color:
                                selectedSection === index
                                  ? "#1E293B"
                                  : "#475569",
                              fontFamily:
                                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              lineHeight: "1.4",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {formatStatuteSection(result.Definition)}
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                      />
                    </Box>
                  </ListItemButton>
                  {index < sections[0].related_data.length - 1 && (
                    <Divider
                      sx={{
                        margin: "4px 24px",
                        borderColor: "#F1F5F9",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default StatuteDetailSidebar;
